import { useEffect, useRef, useState, type MutableRefObject } from "react";
import {
  EditBase,
  Form,
  useDataProvider,
  useRecordContext,
  type RaRecord,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { Navigate } from "react-router";

import type { CampaignTargetDraft } from "../types";
import { useIsCampaignAdmin } from "./useIsCampaignAdmin";
import { CampaignInputs } from "./CampaignInputs";
import { loadCampaignTargets, replaceCampaignTargets } from "./campaignTargets";

const CampaignEditBody = ({
  targetsRef,
}: {
  targetsRef: MutableRefObject<CampaignTargetDraft[]>;
}) => {
  const record = useRecordContext<RaRecord>();
  const dataProvider = useDataProvider();
  const [targets, setTargets] = useState<CampaignTargetDraft[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!record?.id || loaded) return;
    loadCampaignTargets(dataProvider, record.id)
      .then((rows) => {
        setTargets(rows);
        targetsRef.current = rows;
      })
      .finally(() => setLoaded(true));
  }, [dataProvider, loaded, record?.id, targetsRef]);

  const handleTargetsChange = (next: CampaignTargetDraft[]) => {
    setTargets(next);
    targetsRef.current = next;
  };

  return (
    <div className="mt-2 flex lg:mr-72">
      <div className="flex-1">
        <Form>
          <Card>
            <CardContent>
              <CampaignInputs
                targets={targets}
                onTargetsChange={handleTargetsChange}
              />
              <div className="sticky flex pt-4 pb-4 md:pb-0 bottom-0 flex-row justify-end gap-2">
                <CancelButton />
                <SaveButton />
              </div>
            </CardContent>
          </Card>
        </Form>
      </div>
    </div>
  );
};

export const CampaignEdit = () => {
  const isAdmin = useIsCampaignAdmin();
  const dataProvider = useDataProvider();
  const targetsRef = useRef<CampaignTargetDraft[]>([]);

  if (!isAdmin) {
    return <Navigate to="/campaigns" replace />;
  }

  return (
    <EditBase
      redirect="show"
      mutationOptions={{
        onSuccess: async (record) => {
          await replaceCampaignTargets(
            dataProvider,
            record.id,
            targetsRef.current,
          );
        },
      }}
    >
      <CampaignEditBody targetsRef={targetsRef} />
    </EditBase>
  );
};
